import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminDocs.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function AdminDocs() {
  const [scripts, setScripts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedScript, setSelectedScript] = useState('');
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({
    parent_id: '',
    name: '',
    content: '',
    is_code: false,
    order_index: 0
  });
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchScripts();
    fetchCategories();
  }, []);

  const fetchScripts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/scripts`, { withCredentials: true });
      setScripts(response.data);
    } catch (error) {
      console.error('Error fetching scripts:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/documentation/categories`, { withCredentials: true });
      setCategories(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setLoading(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!selectedScript) {
      alert('Please select a script first');
      return;
    }

    try {
      if (editingCategory) {
        await axios.put(`${API_URL}/api/documentation/categories/${editingCategory.id}`, 
          categoryFormData,
          { withCredentials: true }
        );
        alert('Category updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/documentation/categories`, 
          { ...categoryFormData, script_id: selectedScript },
          { withCredentials: true }
        );
        alert('Category created successfully!');
      }
      
      fetchCategories();
      resetCategoryForm();
    } catch (error) {
      alert('Error saving category: ' + error.response?.data?.error);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      parent_id: category.parent_id || '',
      name: category.name,
      content: category.content || '',
      is_code: category.is_code || false,
      order_index: category.order_index
    });
    setShowCategoryForm(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category and all subcategories?')) return;

    try {
      await axios.delete(`${API_URL}/api/documentation/categories/${categoryId}`, { withCredentials: true });
      alert('Category deleted successfully!');
      fetchCategories();
    } catch (error) {
      alert('Error deleting category: ' + error.response?.data?.error);
    }
  };

  const resetCategoryForm = () => {
    setCategoryFormData({
      parent_id: '',
      name: '',
      content: '',
      is_code: false,
      order_index: 0
    });
    setEditingCategory(null);
    setShowCategoryForm(false);
  };

  const getScriptCategories = (scriptId) => {
    return categories.filter(cat => cat.script_id === parseInt(scriptId));
  };

  const buildCategoryTree = (scriptId, parentId = null) => {
    return categories
      .filter(cat => cat.script_id === parseInt(scriptId) && cat.parent_id === parentId)
      .sort((a, b) => a.order_index - b.order_index)
      .map(cat => ({
        ...cat,
        children: buildCategoryTree(scriptId, cat.id)
      }));
  };

  const renderCategoryTree = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;

    return (
      <div key={category.id} style={{ marginLeft: `${level * 20}px` }} className="category-item">
        <div className="category-header">
          <div>
            <span className="category-icon">{hasChildren ? '📁' : '📄'}</span>
            <strong>{category.name}</strong>
            {category.content && <span className="has-content-badge">Has Content</span>}
            {category.is_code && <span className="code-badge">Code</span>}
            <span className="order-badge">Order: {category.order_index}</span>
          </div>
          <div className="category-actions">
            <button onClick={() => handleEditCategory(category)} className="btn btn-secondary btn-sm">
              Edit
            </button>
            <button onClick={() => handleDeleteCategory(category.id)} className="btn btn-danger btn-sm">
              Delete
            </button>
          </div>
        </div>
        {category.content && (
          <p className="category-preview">
            {category.is_code ? '(Code) ' : ''}
            {category.content.substring(0, 100)}...
          </p>
        )}
        {hasChildren && (
          <div className="category-children">
            {category.children.map(child => renderCategoryTree(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="admin-docs">
      <div className="panel-header">
        <h1>📚 Manage Documentation</h1>
        <p>Create categories and subcategories for your scripts</p>
      </div>

      {/* Script Selection */}
      <div className="glass" style={{ padding: '24px', marginBottom: '24px' }}>
        <h2>Select Script</h2>
        <select 
          value={selectedScript} 
          onChange={(e) => setSelectedScript(e.target.value)}
          className="form-control"
        >
          <option value="">Choose a script...</option>
          {scripts.map((script) => (
            <option key={script.id} value={script.id}>
              {script.title}
            </option>
          ))}
        </select>
      </div>

      {/* Categories Section */}
      {selectedScript && (
        <div className="glass" style={{ padding: '24px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2>Categories</h2>
            <button onClick={() => setShowCategoryForm(true)} className="btn btn-primary">
              Add Category
            </button>
          </div>

          {loading ? (
            <div className="loading">Loading categories...</div>
          ) : buildCategoryTree(selectedScript).length === 0 ? (
            <div className="empty-state">
              <p>No categories yet. Create your first category!</p>
            </div>
          ) : (
            <div className="categories-tree">
              {buildCategoryTree(selectedScript).map(cat => renderCategoryTree(cat))}
            </div>
          )}
        </div>
      )}

      {/* Category Form Modal */}
      {showCategoryForm && selectedScript && (
        <div className="modal-overlay" onClick={() => resetCategoryForm()}>
          <div className="modal-content glass" onClick={(e) => e.stopPropagation()}> 
            <h2>{editingCategory ? 'Edit Category' : 'Add Category'}</h2>
            <form onSubmit={handleCreateCategory}>
              <div className="form-group">
                <label>Parent Category (Optional)</label>
                <select
                  value={categoryFormData.parent_id}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, parent_id: e.target.value })}
                >
                  <option value="">No Parent (Root Level)</option>
                  {getScriptCategories(selectedScript)
                    .filter(cat => !editingCategory || cat.id !== editingCategory.id)
                    .map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  placeholder="e.g., Configuration, Items, Config"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Content (Optional for parent categories)</label>
                <textarea
                  placeholder="Leave empty for parent categories. Add content for final categories."
                  value={categoryFormData.content}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, content: e.target.value })}
                  rows="10"
                />
                <small>Supports markdown and emoji shortcodes like :rocket:</small>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={categoryFormData.is_code}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, is_code: e.target.checked })}
                  />
                  Display as Code Block
                </label>
                <small>Check this if the content should be displayed as formatted code</small>
              </div>

              <div className="form-group">
                <label>Order Index</label>
                <input
                  type="number"
                  value={categoryFormData.order_index}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, order_index: e.target.value })}
                />
                <small>Lower numbers appear first</small>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingCategory ? 'Update Category' : 'Create Category'}
                </button>
                <button type="button" onClick={resetCategoryForm} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDocs;
